import { CircleHelp, CloudUpload, FileText, Trash2 } from "lucide-react";
import React, { useRef, useState } from "react";
import { toast } from "sonner";

import Tooltip from "../../lib/ui/components/tooltip/Tooltip";
import { XsmallText } from "../../lib/ui/components/typography/Typography";
import { JOB_DOCUMENT, JOB_DOCUMENTS } from "../../lib/urls";

const ACCEPTED_DOCUMENTS = ".pdf,.docx";

const JobDocumentsUpload = ({
    documents,
    onChange,
    audience,
    companyId,
    jobId,
    token,
    disabled = false,
    dataCy = "job-documents-input",
    contextCount,
    contextLimit = 60000,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const unavailable = disabled || isUploading || !companyId || !jobId;

    const addDocuments = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length || unavailable) return;
        setIsUploading(true);
        let next = [...documents];
        try {
            for (const file of files) {
                const body = new FormData();
                body.append("audience", audience);
                body.append("file", file);
                const response = await fetch(JOB_DOCUMENTS(companyId, jobId), {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                    body,
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(data.detail || `Could not upload ${file.name}`);
                next = [...next, data];
                onChange(next);
            }
            toast.success(files.length === 1 ? "Document uploaded" : "Documents uploaded");
        } catch (error) {
            toast.error(error.message || "Document upload failed");
        } finally {
            setIsUploading(false);
        }
    };

    const removeDocument = async (document) => {
        const response = await fetch(JOB_DOCUMENT(companyId, jobId, document.id), {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
            toast.error("Could not remove the document");
            return;
        }
        onChange(documents.filter((item) => item.id !== document.id));
    };

    return (
        <div>
            {Number.isFinite(contextCount) && (
                <XsmallText className="mb-2">
                    Role context: {contextCount.toLocaleString()} of {contextLimit.toLocaleString()} characters
                </XsmallText>
            )}
            <div
                onDragOver={(event) => {
                    event.preventDefault();
                    if (!unavailable) setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    addDocuments(event.dataTransfer.files);
                }}
                className={`flex flex-col items-center gap-2 py-6 border-2 border-dashed rounded-lg transition-colors ${
                    isDragging ? "border-primary bg-purple-50" : "border-gray-300 bg-gray-50"
                }`}
            >
                <CloudUpload size={24} className="text-gray-500" />
                <XsmallText>
                    {isUploading ? "Uploading…" : "Drag files here or "}
                    {!isUploading && (
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={unavailable}
                            className="font-semibold text-primary hover:underline disabled:opacity-50"
                        >
                            browse
                        </button>
                    )}
                </XsmallText>
                {!jobId && <XsmallText>Create the role before attaching documents.</XsmallText>}
                <div className="flex items-center">
                    <XsmallText>PDF or Word (.docx), up to 10 MB</XsmallText>
                    <Tooltip position="top" content="Text is extracted immediately. Scanned PDFs without a text layer and files that exceed the combined 60,000-character role-context limit are rejected.">
                        <CircleHelp size="1em" className="text-gray-500 cursor-pointer ml-2" />
                    </Tooltip>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPTED_DOCUMENTS}
                    disabled={unavailable}
                    className="hidden"
                    data-cy={dataCy}
                    onChange={(event) => {
                        addDocuments(event.target.files);
                        event.target.value = "";
                    }}
                />
            </div>
            {documents.length > 0 && (
                <ul className="mt-3 flex flex-col gap-2">
                    {documents.map((document) => (
                        <li key={document.id} className="flex items-center gap-3 px-3 py-2 bg-white border border-gray-200 rounded-md">
                            <FileText size="1em" className="text-gray-500 shrink-0" />
                            <span className="flex-1 min-w-0 truncate text-small text-black">{document.file_name}</span>
                            <XsmallText>{(document.character_count || 0).toLocaleString()} chars</XsmallText>
                            <button
                                type="button"
                                onClick={() => removeDocument(document)}
                                disabled={unavailable}
                                aria-label={`Remove ${document.file_name}`}
                                className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                            >
                                <Trash2 size="1em" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default React.memo(JobDocumentsUpload);
